import React, { useState, useRef, useEffect } from 'react';
import { Icon, Button } from 'semantic-ui-react';
import { useNotifications } from '../hooks/useNotifications';
import { formatDateOnly } from '../utils/timezone.utils';
import './NotificationCenter.css';

const NotificationCenter = ({ userEmail, user, roles = [], notificationsEnabled = true }) => {
  const {
    notifications,
    unreadCount,
    connected,
    markAsRead,
    markAllAsRead,
    clearAll,
    getNotifications
  } = useNotifications(userEmail, notificationsEnabled);

  // Show disconnected indicator if notifications are disabled OR socket is disconnected
  const showDisconnected = !notificationsEnabled || !connected;

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all notifications?')) {
      clearAll();
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'request:created':
        return 'plus circle';
      case 'request:status:changed':
        return 'exchange';
      case 'request:needs_approval':
        return 'hand paper';
      case 'vm:creation:progress':
        return 'spinner';
      case 'vm:creation:completed':
        return 'check circle';
      case 'vm:creation:failed':
        return 'times circle';
      default:
        return 'bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'request:created':
        return '#35B7F4'; // Secondary
      case 'request:status:changed':
        return '#6F9911'; // Green
      case 'request:needs_approval':
        return '#F59E0B'; // Amber
      case 'vm:creation:progress':
        return '#6366F1'; // Indigo
      case 'vm:creation:completed':
        return '#6F9911'; // Green
      case 'vm:creation:failed':
        return '#CE1B4E'; // Red
      default:
        return '#6B7280'; // Gray
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    // Use timezone utility for dates older than 7 days
    return formatDateOnly(timestamp);
  };

  // Check if user is DevOps
  const isSuperAdmin = () => {
    return roles.includes('DEVOPS');
  };

  const handleViewAllEvents = () => {
    // Dispatch custom event to switch to All Events tab
    window.dispatchEvent(new CustomEvent('switchToAllEventsTab'));
    setIsOpen(false); // Close notification dropdown
  };

  const recentNotifications = getNotifications(20);

  return (
    <div className="notification-center" ref={dropdownRef}>
      <div
        className={`notification-bell ${isOpen ? 'active' : ''} ${!notificationsEnabled ? 'disabled' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title={notificationsEnabled ? "Notifications" : "Notifications (disabled)"}
      >
        <Icon name="bell" size="large" style={{ margin: 0 }} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {showDisconnected && (
          <div className="notification-offline-indicator" title={notificationsEnabled ? "Disconnected" : "Notifications disabled"}>
            <Icon name="circle" size="tiny" />
          </div>
        )}
      </div>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <div>
              <h3>Notifications</h3>
              {!notificationsEnabled && (
                <div style={{ fontSize: '0.75em', color: '#F59E0B', marginTop: '4px', fontWeight: 500 }}>
                  Notifications are disabled in preferences
                </div>
              )}
              {notificationsEnabled && !connected && (
                <div style={{ fontSize: '0.75em', color: '#F59E0B', marginTop: '4px', fontWeight: 500 }}>
                  Disconnected - reconnecting...
                </div>
              )}
            </div>
            <div className="notification-actions">
              {unreadCount > 0 && (
                <Button
                  size="tiny"
                  basic
                  onClick={handleMarkAllAsRead}
                  style={{ marginRight: '8px' }}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  size="tiny"
                  basic
                  onClick={handleClearAll}
                >
                  Clear all
                </Button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {recentNotifications.length === 0 ? (
              <div className="notification-empty">
                <Icon name="bell slash" size="big" />
                <p>No notifications</p>
                {!notificationsEnabled && (
                  <p style={{ fontSize: '0.85em', marginTop: '8px', color: '#9CA3AF' }}>
                    Enable notifications in preferences to receive updates
                  </p>
                )}
              </div>
            ) : (
              recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                  style={{ cursor: 'default' }}
                >
                  <div
                    className="notification-icon"
                    style={{ color: getNotificationColor(notification.type) }}
                  >
                    <Icon name={getNotificationIcon(notification.type)} />
                  </div>
                  <div className="notification-content">
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">{formatTimestamp(notification.createdAt)}</div>
                  </div>
                  {!notification.read && (
                    <div className="notification-unread-dot" />
                  )}
                </div>
              ))
            )}
          </div>

          {recentNotifications.length > 0 && (
            <div className="notification-footer">
              <span>{notifications.length} total notification{notifications.length !== 1 ? 's' : ''}</span>
            </div>
          )}

          {isSuperAdmin() && (
            <div className="notification-footer" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px' }}>
              <Button
                size="tiny"
                basic
                fluid
                onClick={handleViewAllEvents}
                style={{ marginTop: '4px' }}
              >
                <Icon name="list" />
                View All Events
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;

